import React, { useEffect, useState } from "react";
import { TfiAngleDown, TfiAngleUp } from "react-icons/tfi";
import MenuImage from "../../../helper/images/menuimage.png";
import VegIcon from "../../../helper/svg/Veg.svg";
import { MdStar } from "react-icons/md";
import { getAllCategories, getHotelMenu, updateCartItemQuantity, getHotelCart } from "../../../api/user/hotels";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Pagination from "../../../components/common/Pagination";
import NonVegIcon from "../../../helper/svg/NonVeg.svg";
import toast from "react-hot-toast";
import { ClipLoader } from "react-spinners";
import { useTranslation } from "../../../hooks/useTranslation";

const AccordionSection = ({
  title,
  children,
  index,
  openIndex,
  setOpenIndex,
}) => {
  const isOpen = openIndex === index;


  const toggleAccordion = () => {
    setOpenIndex(isOpen ? null : index);
  };

  return (
    <div className="py-[25px] border-b border-[#DDDDDD] ">
      <div
        className="flex justify-between text-xl font-semibold cursor-pointer"
        onClick={toggleAccordion}
      >
        {title}
        <span>
          {isOpen ? <TfiAngleUp size={20} /> : <TfiAngleDown size={20} />}
        </span>
      </div>
      {isOpen && <div>{children}</div>}
    </div>
  );
};

const Menu = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [page, setPage] = useState(1)
  const limit = 5;
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    categoryId: ""
  })
  const [countMap, setCountMap] = useState({});
  const [notesMap, setNotesMap] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [modalQuantity, setModalQuantity] = useState("");
  const [loadingItems, setLoadingItems] = useState({});
  const queryClient = useQueryClient();
  // const location = useLocation();
  // const { hotelId , bookingId, roomId, guest } = location.state || {};
  const location = useLocation();
  const [searchParams] = useSearchParams();

  let { hotelId, bookingId, roomId, guest } = location.state || {};

  // fallback to query params if state is missing
  hotelId = hotelId || searchParams.get("hotelId");
  bookingId = bookingId || searchParams.get("bookingId");
  roomId = roomId || searchParams.get("roomId");
  guest = guest ?? (searchParams.get("guest") === "true" || searchParams.get("guest") === "1");



  const { t } = useTranslation()

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["hotel-menu-categories", hotelId],
    queryFn: () => getAllCategories(hotelId),
    keepPreviousData: true,
    enabled: !!hotelId
  });

  const {
    data: hotelMenuData,
  } = useQuery({
    queryKey: ["hotel-menu", formData.categoryId, page, limit],
    queryFn: () => getHotelMenu(formData.categoryId, page, limit),
    keepPreviousData: true,
    enabled: !!formData.categoryId
  });

  // Fetch cart on page load
  const { data: cartData } = useQuery({
    queryKey: ["hotel-cart", hotelId, bookingId, roomId],
    queryFn: () => getHotelCart({ hotelId, bookingId, roomId }),
    enabled: !!hotelId,
    retry: false,
  });

  const categories = data?.data?.categories || [];
  const hotelMenu = hotelMenuData?.data?.items || [];
  const pagination = hotelMenuData?.data?.pagination || {};

  // Initialize countMap and cartItems from fetched cart
  useEffect(() => {
    if (cartData?.data?.cart?.items) {
      const cartItemsFromServer = cartData.data.cart.items;
      const newCountMap = {};
      const newCartItems = [];
      const newNotesMap = {};

      cartItemsFromServer.forEach((item) => {
        const itemId = item.itemId?._id || item.itemId;
        newCountMap[itemId] = item.quantity || 0;
        newNotesMap[itemId] = item.note || "";
        newCartItems.push({
          itemId,
          quantity: item.quantity || 0,
          note: item.note || ""
        });
      });

      setCountMap(newCountMap);
      setCartItems(newCartItems);
      setNotesMap(newNotesMap);
    } else if (cartData?.data?.cart === null) {
      // Cart is empty, reset state
      setCountMap({});
      setCartItems([]);
      setNotesMap({});
    }
  }, [cartData]);

  useEffect(() => {
    if (openIndex) {
      setFormData((prev) => ({
        ...prev,
        categoryId: openIndex,
      }));
    }
  }, [openIndex]);



  const handleNotesChange = (itemId, note) => {
    setNotesMap((prev) => ({
      ...prev,
      [itemId]: note,
    }));
  };

  const handleAddToCart = (itemId, quantity) => {
    const note = notesMap[itemId] || "";

    // if (!reservationId) {
    //   toast.error("Missing reservation ID.");
    //   return;
    // }

    setCartItems((prev) => {
      if (quantity === 0) {
        return prev.filter((item) => item.itemId !== itemId);
      }

      const existingIndex = prev.findIndex((item) => item.itemId === itemId);
      const updatedItem = { itemId, quantity, note };

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = updatedItem;
        return updated;
      }
      return [...prev, updatedItem];
    });
  };

  const isItemInCart = (itemId) => {
    return cartItems.some((item) => item.itemId === itemId);
  };

  const updateQuantityMutation = useMutation({
    mutationFn: updateCartItemQuantity,
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to update quantity");
    },
  });

  const handleAddClick = (itemId) => {
    // Show modal to ask for quantity
    setSelectedItemForModal(itemId);
    setModalQuantity("");
    setShowQuantityModal(true);
  };

  const handleConfirmAddToCart = () => {
    if (!selectedItemForModal) return;

    const quantityToAdd = parseInt(modalQuantity, 10);

    if (!quantityToAdd || quantityToAdd <= 0) {
      toast.error(t("restaurantmenu").pleaseEnterValidQuantity || "Please enter a valid quantity");
      return;
    }

    const currentQuantity = countMap[selectedItemForModal] || 0;
    const newTotalQuantity = currentQuantity + quantityToAdd;

    // Store values before clearing modal
    const itemId = selectedItemForModal;
    const note = notesMap[selectedItemForModal] || "";

    // Close modal
    setShowQuantityModal(false);
    setModalQuantity("");

    // Show loader
    setLoadingItems((prev) => ({ ...prev, [selectedItemForModal]: true }));

    // Update cart with new total quantity
    updateQuantityMutation.mutate(
      {
        hotelId,
        itemId: selectedItemForModal,
        quantity: newTotalQuantity,
        note: notesMap[selectedItemForModal] || "",
        ...(bookingId && { bookingId }),
        ...(roomId && { roomId }),
      },
      {
        onSuccess: (response) => {
          // Invalidate cart query to refetch latest data
          queryClient.invalidateQueries(["hotel-cart", hotelId, bookingId, roomId]);
          
          // Use the actual quantity from backend response
          const savedCart = response?.data?.cart;
          if (savedCart && savedCart.items) {
            const updatedItem = savedCart.items.find(item => {
              const savedItemId = item.itemId?._id || item.itemId;
              return String(savedItemId) === String(itemId);
            });
            if (updatedItem) {
              const actualQuantity = updatedItem.quantity || 0;
              
              // Update countMap with actual quantity from server
              setCountMap(prev => ({
                ...prev,
                [itemId]: actualQuantity
              }));

              // Update local cart state
              setCartItems(prev => {
                const updatedCartItems = [...prev];
                const existingIndex = updatedCartItems.findIndex(item => item.itemId === itemId);

                if (existingIndex >= 0) {
                  if (actualQuantity > 0) {
                    updatedCartItems[existingIndex] = {
                      itemId,
                      quantity: actualQuantity,
                      note
                    };
                  } else {
                    updatedCartItems.splice(existingIndex, 1);
                  }
                } else if (actualQuantity > 0) {
                  updatedCartItems.push({
                    itemId,
                    quantity: actualQuantity,
                    note
                  });
                }

                return updatedCartItems;
              });
            } else if (newTotalQuantity === 0) {
              // Item was removed
              setCountMap(prev => {
                const updated = { ...prev };
                delete updated[itemId];
                return updated;
              });
              setCartItems(prev => prev.filter(item => item.itemId !== itemId));
            }
          } else if (newTotalQuantity > 0) {
            // Fallback: if no cart returned but we sent quantity > 0, use calculated value
            setCountMap(prev => ({
              ...prev,
              [itemId]: newTotalQuantity
            }));
            setCartItems(prev => {
              const existingIndex = prev.findIndex(item => item.itemId === itemId);
              if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = { itemId, quantity: newTotalQuantity, note };
                return updated;
              } else {
                return [...prev, { itemId, quantity: newTotalQuantity, note }];
              }
            });
          }

          toast.success(t("restaurantmenu").addedItemsToCart?.replace("{{count}}", quantityToAdd) || `Added ${quantityToAdd} item(s) to cart`);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || t("restaurantmenu").failedAddItems);
        },
        onSettled: () => {
          setLoadingItems((prev) => ({ ...prev, [selectedItemForModal]: false }));
          setSelectedItemForModal(null);
        },
      }
    );
  };

  const handleCloseModal = () => {
    setShowQuantityModal(false);
    setSelectedItemForModal(null);
    setModalQuantity("");
  };

  const handleIncrement = (itemId) => {
    const currentQty = countMap[itemId] || 0;
    const newQuantity = currentQty + 1;
    handleAddToCart(itemId, newQuantity);
    setCountMap((prev) => ({ ...prev, [itemId]: newQuantity }));
  };

  const handleDecrement = (itemId) => {
    const currentQty = countMap[itemId] || 0;
    const newQuantity = currentQty > 0 ? currentQty - 1 : 0;
    handleAddToCart(itemId, newQuantity);
    setCountMap((prev) => ({ ...prev, [itemId]: newQuantity }));
  };

  const selectedMenuItem = hotelMenu?.find(item => item._id === selectedItemForModal);

  return (
    <div className="max-w-6xl mx-auto p-6 mt-8 mb-8">
      {/* Quantity Input Modal */}
      {showQuantityModal && selectedMenuItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md p-6 md:p-8 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-black">
                {t("restaurantmenu").addToCart}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-3 mb-4">
                {selectedMenuItem?.images?.length > 0 ? (
                  <img
                    src={selectedMenuItem.images[0]}
                    alt={selectedMenuItem.itemName}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <img
                    src={MenuImage}
                    alt={t("restaurantmenu").menu || "Menu"}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                )}
                <div>
                  <h4 className="text-lg font-semibold text-black">
                    {selectedMenuItem.itemName}
                  </h4>
                  <p className="text-base text-gray-600">
                    ₾{selectedMenuItem.price}
                  </p>
                </div>
              </div>

              {countMap[selectedItemForModal] > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    {t("restaurantmenu").currentQuantityInCart || "Current quantity in cart:"} <span className="font-semibold text-black">{countMap[selectedItemForModal]}</span>
                  </p>
                </div>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("restaurantmenu").quantityToAdd || "Quantity to add"}
              </label>
              <input
                type="number"
                min="1"
                value={modalQuantity}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setModalQuantity(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmAddToCart();
                  } else if (e.key === 'Escape') {
                    handleCloseModal();
                  }
                }}
                placeholder={t("restaurantmenu").enterQuantity || "Enter quantity"}
                className="w-full border border-gray-300 rounded-[10px] p-4 text-base focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
                autoFocus
              />

              {modalQuantity && parseInt(modalQuantity, 10) > 0 && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-700">
                    {t("restaurantmenu").totalQuantityWillBe || "Total quantity will be:"} <span className="font-bold text-[#4CAF50] text-lg">
                      {(countMap[selectedItemForModal] || 0) + parseInt(modalQuantity, 10)}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ({t("restaurantmenu").current || "Current"}: {countMap[selectedItemForModal] || 0} + {t("restaurantmenu").new || "New"}: {parseInt(modalQuantity, 10)})
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCloseModal}
                className="flex-1 py-3 px-4 rounded-[10px] bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors"
              >
                {t("restaurantmenu").cancel || t("common")?.cancel || "Cancel"}
              </button>
              <button
                onClick={handleConfirmAddToCart}
                disabled={!modalQuantity || parseInt(modalQuantity, 10) <= 0}
                className="flex-1 py-3 px-4 rounded-[10px] bg-[#4CAF50] text-white font-semibold hover:bg-[#209b25] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("restaurantmenu").addToCart}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex gap-[10px] items-center">

        <button
          className="py-[8px] px-[30px] leading-[30px] rounded-full sm:text-lg text-sm font-medium text-white bg-[#4CAF50]"
        >
          {t("restaurantmenu").veg || "Veg"}
        </button>
        <button
          className="py-[6px] px-[30px] leading-[30px] rounded-full sm:text-lg text-sm font-medium text-[#FF0000]  border border-[#FF0000]"
        >
          {t("restaurantmenu").nonVeg || "Non-veg"}
        </button>

        {/* {
            restaurant?.foodType === "veg" ? <button
              className="py-[8px] px-[30px] leading-[30px] rounded-full sm:text-lg text-sm font-medium text-white bg-[#4CAF50]"
            >
              Veg
            </button> : restaurant?.foodType === "non-veg" ? <button
              className="py-[6px] px-[30px] leading-[30px] rounded-full sm:text-lg text-sm font-medium text-[#FF0000]  border border-[#FF0000]"
            >
              Non-veg
            </button> : <>
              <button
                className="py-[8px] px-[30px] leading-[30px] rounded-full sm:text-lg text-sm font-medium text-white bg-[#4CAF50]"
              >
                Veg
              </button>
              <button
                className="py-[6px] px-[30px] leading-[30px] rounded-full sm:text-lg text-sm font-medium text-[#FF0000]  border border-[#FF0000]"
              >
                Non-veg
              </button>
            </>
          } */}


        {cartItems?.length > 0 && (
          <button
            onClick={() => navigate(`/hotel-cart`, {
              state: { cartItems, hotelId, bookingId, roomId, guest }
            })}
            className="py-[8px] px-[30px] leading-[30px] rounded-full sm:text-lg text-sm font-medium text-white bg-green-500 ml-auto"
          >
            {t("restaurantmenu").checkout || t("cart").checkout || "Checkout"}
          </button>
        )}
      </div>
      {
        categories?.length > 0 ? categories?.map((item) => {
          return <AccordionSection
            index={item?._id}
            openIndex={openIndex}
            setOpenIndex={setOpenIndex}
            title={`${item?.category} (${item?.totalMenuItems})`}
          >


            {
              hotelMenu?.length > 0 && hotelMenu?.map((item) => {
                return (
                  <>
                    <div className="lg:items-center items-start justify-between grid lg:grid-cols-[1fr_auto] grid-cols-1 gap-[24px] bg-white border border-[#F1F1F1] rounded-[5px] lg:py-[10px] lg:pl-[11px] lg:pr-[20px] p-[15px] mt-[20px] menu-shadow">
                      <div className="flex lg:gap-[47px] gap-[10px] lg:items-center  lg:pr-[24px] lg:border-r border-[#DDDDDD]">
                        <img
                          src={item?.images?.length > 0 ? `${item.images[0]}` : MenuImage}
                          className="md:max-w-[300px] max-w-[91px] xl:h-full rounded-[5px] object-cover"
                          alt={t("restaurantmenu").menu || "Menu"}
                        />
                        <div className="">
                          {
                            item?.foodType === "veg"
                              ? <img src={VegIcon} alt={t("restaurantmenu").vegIcon || "Veg Icon"} />
                              : <img src={NonVegIcon} alt={t("restaurantmenu").nonVegIcon || "Non Veg Icon"} />
                          }

                          <h3 className="font-semibold lg:text-[30px] md:text-[25px] text-lg text-black md:my-[15px] mt-[5px]">
                            {item?.itemName}
                          </h3>

                          <div className="flex items-center lg:gap-[37px] gap-[6px] md:mt-3 mt-[5px] max-w-[259px]">
                            <span className="lg:text-[22px] text-base font-medium text-black">
                              ₾{item?.price}
                            </span>
                            <div className="flex items-center md:gap-[15px] gap-[9px]">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <MdStar key={i} size={21} className="text-[#FF9D00]" />
                                ))}
                              </div>
                              <p className="text-[#4CAF50] text-[14px] font-medium">
                                4.3 (7)
                              </p>
                            </div>
                          </div>

                          <p className="md:text-lg text-[15px] text-[#848484] md:mt-[19px] mt-[15px] md:leading-[30px] leading-6">
                            {item?.description}
                          </p>
                        </div>
                      </div>


                      <div>
                        <div className="grid grid-cols-[auto_1fr]  gap-[10px] items-center justify-between">
                          <span className="text-lg text-black">{t("restaurantmenu").selectQuantity || "Select Quantity"}</span>
                          <div className="w-full max-w-[216px] ml-auto">
                            <div className="flex items-center justify-between border border-gray-300 rounded-full px-4 py-2 ">
                              <button
                                onClick={() => handleDecrement(item._id)}
                                className="text-lg px-2 text-gray-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={loadingItems[item._id]}
                              >
                                {loadingItems[item._id] ? (
                                  <ClipLoader size={16} color="#4CAF50" />
                                ) : (
                                  "–"
                                )}
                              </button>
                              <span className="mx-4 text-base">{countMap[item._id] || 0}</span>
                              <button
                                onClick={() => handleIncrement(item._id)}
                                className="text-lg px-2 text-gray-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={loadingItems[item._id]}
                              >
                                {loadingItems[item._id] ? (
                                  <ClipLoader size={16} color="#4CAF50" />
                                ) : (
                                  "+"
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddClick(item._id)}
                          className="mt-[17px] mb-5 w-full rounded-[2px] bg-[#4CAF50] 2xl:text-lg text-base text-white text-center font-bold 2xl:py-[14px] 2xl:px-[20px] p-3 green-shadow hover:bg-[#209b25] disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={loadingItems[item._id]}
                        >
                          {loadingItems[item._id] ? t("common").loading : t("restaurantmenu").addToCart}
                        </button>
                        <textarea
                          className="w-full min-h-[115px] border border-[#DDDDDD] rounded-[10px] p-[24px] shadow-sm focus:outline-none focus:border-[#4CAF50]"
                          placeholder={t("restaurantmenu").notesPlaceholder || 'e.g., "No pepper," "Extra cheese"'}
                          value={notesMap[item._id] || ""}
                          onChange={(e) => handleNotesChange(item._id, e.target.value)}
                        ></textarea>
                      </div>

                    </div>
                  </>
                );
              })
            }

            {hotelMenu?.length > 0 && (
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                setPage={setPage}
              />
            )}

          </AccordionSection>
        }) : <div className="flex justify-center mt-10">
          <div className=" text-[#856404] px-6 py-3 rounded-md   text-lg font-semibold tracking-wide">
            {t("restaurantmenu").noMenuItems || "No Menu Items Found"}
          </div>
        </div>

      }
    </div>
  );
};

export default Menu;
